import React, { useCallback, useMemo, useState } from 'react';

import WizardSystem, { Step } from 'common/WizardSystem';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { closeMessage } from 'messages';
import { getAllMessages } from 'messages/selectors';
import { getSharedSchema } from '../common/schemas';
import { queryClient } from 'common/providers/QueryProvider';
import { getActivitiesQueryOptions } from './hooks/useGetActivitiesForFields/useGetActivitiesForFields';
import { useCreateActivities } from './hooks/useCreateActivities';
import { useSavePrescriptions } from './hooks/useSavePrescriptions';
import { useSavingStatus } from './hooks/useSavingStatus';
import { getActiveUserId } from 'current/selectors';
import { getMessage } from 'util/locale';
import { activityTypes } from 'constants/activities';

import { FieldSelection } from '../common/FieldSelection';
import MessagePanel from 'messages/MessagePanel';
import { ReviewScripts } from './ReviewScripts';
import { ScriptGeneration } from '../common/ScripGeneration';

import type { Activity } from 'store/types/types';
import type { FormData } from '../common/types';
import type { SubmitHandler } from 'react-hook-form';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { FIELDS_ACTIVITIES_QUERY_KEY } from './hooks/useGetActivitiesForFields/constants';
import { getDateString, parsePrescriptionsToSave } from './utils';
import { useRedirectUrl } from 'util/hooks/useRedirectUrl';
import { navigateToUrl } from 'util/routes';

// The data in the onSubmit function receives the plannedPlantingDate as a Date object,
// even though is converted to a string in the DatePicker
interface SubmitFormData extends Omit<FormData, 'plannedPlantingDate'> {
    plannedPlantingDate: Date;
  }

export default function BulkFieldViewSeedScripts() {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(getAllMessages);
  const userId = useAppSelector(getActiveUserId);
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const {
    allFailed,
    allSaved,
    areErrors,
    isSaving,
    prescriptionSaveStatuses,
    resetPrescriptionStatuses,
    updatePrescriptionStatuses
  } = useSavingStatus();
  const redirectUrl = useRedirectUrl();
  const [isGeneratingPlantingPrescriptions, setIsGeneratingPlantingPrescriptions] = useState(false);

  const handleCloseToast = useCallback(
    (id: string) => dispatch(closeMessage(id)),
    [dispatch]
  );

  const steps: Step[] = useMemo(() => [
    {
      component: <FieldSelection description={`1. ${getMessage('bulk-fvss-description__step_1')}`} />,
      title: getMessage('bulk-fvss-header-title__step_1'),
    },
    {
      component: <ScriptGeneration />,
      title: getMessage('bulk-fvss-header-title__step_2'),
    },
    {
      component: <ReviewScripts
        allFailed={allFailed}
        allSaved={allSaved}
        areErrors={areErrors}
        isSaving={isSaving}
        prescriptionSaveStatuses={prescriptionSaveStatuses}
        resetPrescriptionStatuses={resetPrescriptionStatuses}
        setIsGeneratingPlantingPrescriptions={setIsGeneratingPlantingPrescriptions}
      />,
      title: getMessage('bulk-fvss-header-title__step_3'),
      // TODO: Add validations for this step later. For now, users can click submit button without any issues because
      // the service is mocked.
    },
  ], [allFailed, allSaved, areErrors, isSaving, prescriptionSaveStatuses, resetPrescriptionStatuses]);

  const { mutate: createActivities } = useCreateActivities();
  const { initSavingPrescriptions } = useSavePrescriptions({ updatePrescriptionStatuses });

  const parseAndSavePrescriptions = useCallback(({
    activities,
    fields,
    plannedPlantingDateString
  }: {
    activities: Activity[];
    fields: FormData['fields'];
    plannedPlantingDateString: string;
  }) => {
    const prescriptionsToSave = parsePrescriptionsToSave({
      fields,
      activities,
      cropNominalWeight,
      plannedPlantingDateString,
      bulkPrefix: getMessage('prescription-name-prefix-bulk')
    });

    initSavingPrescriptions(prescriptionsToSave);
  }, [cropNominalWeight, initSavingPrescriptions]);

  const onSubmit = useCallback<SubmitHandler<SubmitFormData>>(async ({ plannedPlantingDate, fields }) => {
    const plannedPlantingDateString = getDateString(plannedPlantingDate);

    const activities = await Promise.all(fields.map(({ id: fieldUuid }) =>
      queryClient.ensureQueryData(getActivitiesQueryOptions({
        activityType: activityTypes.PLANTING,
        fieldUuid,
        userId,
      }))
    ));

    const fieldsWithoutActivity = fields.filter((field, index) => {
      return !activities[index].some((activity) =>
        activity['field-uuid'] === field.id && getDateString(new Date(activity.date)) === plannedPlantingDateString
      );
    });

    if (fieldsWithoutActivity.length) {
      const createActivitiesParams = fieldsWithoutActivity.map(({ id }) => ({
        date: plannedPlantingDate.toISOString(),
        fieldUuid: id,
        type: activityTypes.PLANTING,
        uuid: crypto.randomUUID(),
      }));

      createActivities(createActivitiesParams, {
        onSuccess: () => {
          const updatedActivities = fields.map((field) =>
            queryClient.getQueryData<Activity[]>([FIELDS_ACTIVITIES_QUERY_KEY, activityTypes.PLANTING, field.id]));

          parseAndSavePrescriptions({ activities: updatedActivities.flat(), fields, plannedPlantingDateString });
        },
      });
    } else {
      parseAndSavePrescriptions({ activities: activities.flat(), fields, plannedPlantingDateString });
    }
  }, [createActivities, parseAndSavePrescriptions, userId]);

  const onClose = useCallback(() => {
    navigateToUrl(redirectUrl);
  }, [redirectUrl]);

  return (
    <>
      <MessagePanel
        closeMessage={handleCloseToast}
        isToolbarShown={false}
        messages={messages}
      />
      <WizardSystem
        steps={steps}
        wizardTitle={getMessage('bulk-fvss-wizard-title')}
        onClose={onClose}
        onSubmit={onSubmit}
        schema={getSharedSchema}
        defaultValues={{ fields: [] }}
        disableSaveButton={isGeneratingPlantingPrescriptions}
      />
    </>
  );
}
